import React, { useState, useRef } from 'react';
import cn from 'classnames';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { getCatalogToolScope, processCatalogTool } from '../../../api/catalog_tool';
import './PinDiagnosticPanel.scss';

const ROW_ACTIONS = [
  { value: 'requeue', label: 'Re-queue' },
  { value: 'dismiss', label: 'Dismiss' },
  { value: 'archive', label: 'Archive' }
];

/**
 * Pin diagnostic ("something's wrong with this pin"): load a pin, list the unique domains
 * attached to its matched product, flag domains outside the product's brand, and unlink a
 * whole domain from that product per row.
 */
const PinDiagnosticPanel = () => {
  const [pinIdInput, setPinIdInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [notFoundId, setNotFoundId] = useState(null);
  const [busyDomain, setBusyDomain] = useState(null);
  const [flaggedSelected, setFlaggedSelected] = useState(false);
  const latestLoadId = useRef(0);

  const loadPin = async () => {
    const id = parseInt(pinIdInput, 10);
    if (!id || isLoading) return;
    const loadId = ++latestLoadId.current;
    setIsLoading(true);
    setData(null);
    setNotFoundId(null);
    setFlaggedSelected(false);
    try {
      const response = await getCatalogToolScope({ type: 'pin', id });
      if (loadId !== latestLoadId.current) return;
      if (!response.data.pin) setNotFoundId(id);
      else setData(response.data);
    } catch (error) {
      window.ALERT.error(error || 'Failed to load pin');
    } finally {
      if (loadId === latestLoadId.current) setIsLoading(false);
    }
  };

  // One request for the whole batch so the server's per-run URL cap covers the aggregate —
  // per-domain requests would each get a fresh cap.
  const runDomainAction = async (domains, postAction) => {
    setBusyDomain(domains.length === 1 ? domains[0] : 'all-flagged');
    try {
      const response = await processCatalogTool({ mode: 'unlink_domain', Product_id: data.product.id, domains, postAction, dryRun: false });
      const { processed, pinsUpdated, variantsUpdated, skippedConflicts } = response.data;
      const label = domains.length === 1 ? domains[0] : `${domains.length} domains`;
      window.ALERT.success(
        `${label}: unlinked ${processed} URLs · ${pinsUpdated} pins · ${variantsUpdated} variants${
          skippedConflicts ? ` · ${skippedConflicts} conflicts skipped` : ''
        }`
      );
    } catch (error) {
      window.ALERT.error(error || 'Failed to process domain');
    } finally {
      // Refresh even after a failure — earlier writes may have landed and the counts on
      // screen are already stale.
      setBusyDomain(null);
      await loadPin();
    }
  };

  // Archive is domain-wide (the ignored-domains list), so it alone confirms first.
  const handleAction = (domains, postAction) => {
    if (postAction !== 'archive') return runDomainAction(domains, postAction);
    confirmAlert({
      title: 'Archive entire domains?',
      message: `This hides ${domains.join(', ')} from all matching queues — every URL on ${
        domains.length === 1 ? 'this domain' : 'these domains'
      }, not just this product's. No data is deleted.`,
      buttons: [
        { label: 'Cancel', onClick: () => {} },
        { label: 'Archive', onClick: () => runDomainAction(domains, postAction) }
      ]
    });
  };

  const domains = data?.domains || [];
  const flaggedDomains = domains.filter(row => row.isOutlier).map(row => row.domain);

  return (
    <div className='catalog-pin-diagnostic'>
      <h2>2 · Pin diagnostic</h2>

      <div className='pin-input-row'>
        <input
          type='text'
          inputMode='numeric'
          value={pinIdInput}
          placeholder='Pin ID'
          onChange={e => setPinIdInput(e.target.value.replace(/\D/g, ''))}
          onKeyDown={e => e.key === 'Enter' && loadPin()}
        />
        <button type='button' className='load-button' onClick={loadPin} disabled={isLoading || !pinIdInput}>
          {isLoading ? 'Loading…' : 'Load'}
        </button>
      </div>

      {notFoundId && <div className='pin-not-found'>No pin found with ID {notFoundId}.</div>}

      {data && (
        <>
          <div className='pin-card'>
            <div className='pin-title'>
              <span className='pin-id'>Pin {data.pin.id}</span> {data.pin.title || 'Untitled pin'}
            </div>
            {data.product ? (
              <div className='pin-product'>
                Matched to <span className='pin-id'>PID {data.product.id}</span> {data.product.title}
                {data.product.brandName ? ` · ${data.product.brandName}` : ''}
              </div>
            ) : (
              <div className='pin-product unmatched'>No matched product — outlier detection unavailable.</div>
            )}
            {data.product && !data.outlierDetectionAvailable && (
              <div className='pin-product unmatched'>Product has no brand record — outlier detection unavailable.</div>
            )}
          </div>

          {flaggedDomains.length > 0 && (
            <div className='flagged-bar'>
              {!flaggedSelected ? (
                <button type='button' className='select-flagged' onClick={() => setFlaggedSelected(true)}>
                  Select all flagged ({flaggedDomains.length})
                </button>
              ) : (
                <>
                  <span className='flagged-count'>
                    {flaggedDomains.length} flagged {flaggedDomains.length === 1 ? 'domain' : 'domains'}:
                  </span>
                  {ROW_ACTIONS.map(action => (
                    <button
                      key={action.value}
                      type='button'
                      className='flagged-action'
                      disabled={!!busyDomain}
                      onClick={() => handleAction(flaggedDomains, action.value)}
                    >
                      {action.label}
                    </button>
                  ))}
                  <button type='button' className='flagged-cancel' disabled={!!busyDomain} onClick={() => setFlaggedSelected(false)}>
                    cancel
                  </button>
                </>
              )}
            </div>
          )}

          <ul className='domain-list'>
            {domains.map(row => (
              <li key={row.domain} className={cn({ outlier: row.isOutlier, busy: busyDomain === row.domain })}>
                <span className='domain-name'>
                  {row.isOutlier && <span className='outlier-icon'>⚠</span>}
                  {row.domain}
                </span>
                <span className='domain-counts'>
                  {row.urlCount.toLocaleString()} URLs · {row.pinCount.toLocaleString()} pins
                </span>
                <span className='domain-actions'>
                  {busyDomain === row.domain ? (
                    <span className='domain-busy'>Processing…</span>
                  ) : (
                    ROW_ACTIONS.map(action => (
                      <button
                        key={action.value}
                        type='button'
                        disabled={!!busyDomain}
                        onClick={() => handleAction([row.domain], action.value)}
                      >
                        {action.label}
                      </button>
                    ))
                  )}
                </span>
              </li>
            ))}
            {domains.length === 0 && data.product && <li className='empty'>No domains attached to this product.</li>}
          </ul>
        </>
      )}
    </div>
  );
};

export default PinDiagnosticPanel;
